import React from "react";
import { Stack, Typography, styled, SxProps, Chip, Divider } from "@mui/material";
import { Link, To } from "react-router";
import useCountMaxTags from "../../../Utils/Hooks/useCountMaxTags";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { cacheService, thumbnailService } from "../../../Core/Api/services";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import moment from "moment";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";

export type SearchResultType = {
  title?: string;
  asset: DbAssetObjectInfos;
  onClick: () => void;
};

export type SearchResultProps = {
  sx?: SxProps;
  to: To;
  metadataList: DbMetadataFieldInfos[];
} & SearchResultType;

const SearchResult: React.FC<SearchResultProps> = (props) => {
  const customerExtId = cacheService.GetCustomerExternalIdLabel();

  const tagsTop = [
    GetMetadataByTagName(props.asset, "titleType", props.metadataList)?.value,
    moment(GetMetadataByTagName(props.asset, "productionDate", props.metadataList)?.value).format("YYYY"),
  ];

  let titleOriginalPictureFormat = GetMetadataByTagName(
    props.asset,
    "titleOriginalPictureFormat",
    props.metadataList
  )?.value;

  if (titleOriginalPictureFormat) tagsTop.push(titleOriginalPictureFormat);
  if (tagsTop[0] === "Season")
    tagsTop.push(
      `${GetMetadataByTagName(props.asset, "serieSeasonEpisodeTotalNumber", props.metadataList)?.value || 0} ep`
    );

  let tagsList = [
    "#" + props.asset.id,
    GetMetadataByTagName(props.asset, "titleOriginalLanguage", props.metadataList)?.value?.label,
  ];

  if (customerExtId) tagsList.splice(1, 0, customerExtId + " #" + props.asset.titleCustomerId);

  const { visibleTags, hiddenTagsCount } = useCountMaxTags({
    tagList: tagsList.filter((x) => x !== undefined),
    nbMax: 3,
  });

  return (
    <StyledLink to={props.to} onClick={props.onClick}>
      <figure>
        <img
          src={thumbnailService.GetObjectPosterUrl(props.asset.id, ThumbnailSize.small)}
          alt={props.title}
          loading="lazy"
        />
      </figure>
      <Stack>
        <Typography color="text.primary" variant="body1" component="p" fontWeight={500} sx={{ mb: 0.5 }}>
          {props.title ? props.title : props.asset.name}
        </Typography>
        <>
          <Stack key={"category"} direction="row" spacing={0.5}>
            {tagsTop
              ?.filter((x) => x)
              .map((tag, index) => (
                <Chip size="small" key={`tagtop-${props.asset.id}-${index}`} label={tag} />
              ))}
          </Stack>
          <Stack key={"result"} direction="row" spacing={0.5} sx={{ mt: 1 }}>
            {visibleTags.map((tag, index) => (
              <React.Fragment key={tag}>
                <Typography color="text.secondary" component="span">
                  {tag}
                </Typography>
                {(index < visibleTags.length - 1 || (index === visibleTags.length - 1 && hiddenTagsCount > 0)) && (
                  <Divider orientation="vertical" variant="middle" flexItem />
                )}
              </React.Fragment>
            ))}
            {hiddenTagsCount > 0 && (
              <Typography color="text.secondary" component="span">
                +{hiddenTagsCount}
              </Typography>
            )}
          </Stack>
        </>
      </Stack>
    </StyledLink>
  );
};

export default SearchResult;

const StyledLink = styled(Link)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  borderRadius: 6,
  padding: theme.spacing(0.5),
  overflow: "hidden",
  transition: "background .3s",

  figure: {
    marginRight: theme.spacing(1.5),
    width: 54,
    height: 72,
    borderRadius: 3,
    overflow: "hidden",
    img: {
      height: "100%",
    },
  },

  "&:hover": {
    background: theme.palette.backgroundAlpha.main,
  },
}));
